import React from 'react';

const MemoryOperations = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="doc-header">
        <h1>Memory Operations</h1>
        <p className="doc-description">
          Learn how to work with memories in MemU. This section covers the fundamental operations for creating, storing, and retrieving memories effectively.
        </p>
      </div>

      <div className="doc-section">
        <h2 id="overview">Overview</h2>
        <p>
          Memory operations are the core building blocks of intelligent AI systems in MemU.
          Whether you're building a chatbot, recommendation system, or personalized AI assistant,
          understanding how to effectively manage memories is crucial for success.
        </p>

        <p>
          This section is divided into two main areas:
        </p>

        <ul>
          <li><strong>Add Memory:</strong> Learn how to create and store new memories</li>
          <li><strong>Retrieve Memory:</strong> Master the art of finding and accessing stored memories</li>
        </ul>
      </div>

      <div className="doc-section">
        <h2 id="getting-started">Getting Started</h2>
        <p>
          Choose the operation you want to learn about:
        </p>

        <div className="operation-cards">
          <div className="operation-card-large">
            <button
              className="operation-card-button"
              onClick={() => onSectionChange('add-memory')}
            >
              <div className="card-icon">
                <i className="fa-solid fa-plus-circle"></i>
              </div>
              <div className="card-content">
                <h3>Add Memory</h3>
                <p>
                  Learn how to create, validate, and store memories in MemU.
                  Covers basic creation, batch operations, metadata management, and best practices.
                </p>
                <div className="card-topics">
                  <span className="topic-tag">Basic Creation</span>
                  <span className="topic-tag">Batch Operations</span>
                  <span className="topic-tag">Metadata</span>
                  <span className="topic-tag">Validation</span>
                </div>
              </div>
              <div className="card-arrow">
                <i className="fa-solid fa-arrow-right"></i>
              </div>
            </button>
          </div>

          <div className="operation-card-large">
            <button
              className="operation-card-button"
              onClick={() => onSectionChange('retrieve-memory')}
            >
              <div className="card-icon">
                <i className="fa-solid fa-search"></i>
              </div>
              <div className="card-content">
                <h3>Retrieve Memory</h3>
                <p>
                  Master advanced search techniques, filtering strategies, and optimization tips
                  for finding the right memories at the right time.
                </p>
                <div className="card-topics">
                  <span className="topic-tag">Semantic Search</span>
                  <span className="topic-tag">Temporal Queries</span>
                  <span className="topic-tag">Filtering</span>
                  <span className="topic-tag">Performance</span>
                </div>
              </div>
              <div className="card-arrow">
                <i className="fa-solid fa-arrow-right"></i>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="workflow">Typical Workflow</h2>
        <p>
          Most applications follow a similar pattern when working with memories:
        </p>

        <div className="workflow-steps">
          <div className="workflow-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Create Memories</h4>
              <p>Store user interactions, preferences, and important events</p>
            </div>
          </div>

          <div className="workflow-arrow">
            <i className="fa-solid fa-arrow-down"></i>
          </div>

          <div className="workflow-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Retrieve Relevant Memories</h4>
              <p>Search for memories that match current context or user queries</p>
            </div>
          </div>

          <div className="workflow-arrow">
            <i className="fa-solid fa-arrow-down"></i>
          </div>

          <div className="workflow-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Use in AI Responses</h4>
              <p>Incorporate retrieved memories to provide personalized, contextual responses</p>
            </div>
          </div>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="quick-reference">Quick Reference</h2>
        <div className="quick-ref-grid">
          <div className="quick-ref-card">
            <h4>🚀 Quick Start</h4>
            <p>New to MemU? Start with <strong>Add Memory</strong> to learn the basics of storing information.</p>
          </div>

          <div className="quick-ref-card">
            <h4>🔍 Advanced Search</h4>
            <p>Need to find specific memories? Check out <strong>Retrieve Memory</strong> for powerful search techniques.</p>
          </div>

          <div className="quick-ref-card">
            <h4>📊 Best Practices</h4>
            <p>Both sections include comprehensive best practices and optimization tips.</p>
          </div>

          <div className="quick-ref-card">
            <h4>💡 Examples</h4>
            <p>Each guide includes practical code examples and real-world use cases.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryOperations;

